import { z } from "zod";
import {
  MAIN_PRODUCT_TYPE_SLUGS,
  getDisplayName,
} from "@/lib/generated/product-types";
import {
  getConditionsForProductType,
  getCanonicalConditionValue,
} from "@shared/generated/productConditions";

const QUOTABLE_PRODUCT_TYPES = MAIN_PRODUCT_TYPE_SLUGS.filter(
  (slug) => getConditionsForProductType(slug).length > 0,
);
import type { ModelContextTool } from "@/lib/webmcp/model-context";
import { FORM_MODE_CONFIGS, RFQ_TYPE_CONFIGS } from "../config";
import { createEmptyLocation, type RfqContactInput, type RfqLocationInput } from "../types";
import { resolveRfqCountry } from "../rfq-location-validation";
import { buildValidationConfig, computeRfqMissingFields } from "../use-rfq-form-validation";
import { formatMissingFields } from "../field-names";

export const REQUEST_QUOTE_TOOL_NAME = "request_quote";

const requiredText = (maxLength: number) => z.string().trim().min(1).max(maxLength);

export const requestQuoteToolInputSchema = z.strictObject({
  productType: z
    .enum(QUOTABLE_PRODUCT_TYPES)
    .describe("Packaging type the buyer wants, as a catalog slug."),
  condition: requiredText(40).describe(
    "Condition grade for that packaging type, such as new or used. Must be one of the grades the form offers.",
  ),
  quantity: z.number().int().positive().max(1_000_000).describe("Number of units."),
  delivery: z
    .strictObject({
      line1: requiredText(200),
      line2: z.string().trim().max(200).optional(),
      city: requiredText(100),
      state: requiredText(40).describe("Two-letter US state or Canadian province code."),
      postalCode: requiredText(12),
    })
    .describe("Delivery address."),
  contact: z
    .strictObject({
      firstName: requiredText(100),
      lastName: requiredText(100),
      email: requiredText(254),
      phone: requiredText(40),
      company: z.string().trim().max(200).optional(),
    })
    .describe("The buyer's own contact details. Never another person's."),
  notes: z.string().trim().max(2000).optional().describe("Anything else the supplier should know."),
});

export type RequestQuoteToolInput = z.infer<typeof requestQuoteToolInputSchema>;

export interface RequestQuoteFormFill {
  productTypeSlug: RequestQuoteToolInput["productType"];
  productTypeName: string;
  condition: string;
  notes: string | undefined;
  location: RfqLocationInput;
  contact: RfqContactInput;
}

export type RequestQuoteToolResult =
  | { status: "awaiting_user_submit"; message: string }
  | { status: "invalid_input"; message: string };

const BUY_VALIDATION = buildValidationConfig(
  RFQ_TYPE_CONFIGS.buy,
  FORM_MODE_CONFIGS.anonymous,
);

export function toRequestQuoteFormFill(input: RequestQuoteToolInput): RequestQuoteFormFill {
  const country = resolveRfqCountry(input.delivery.state, undefined);
  return {
    productTypeSlug: input.productType,
    productTypeName: getDisplayName(input.productType) ?? input.productType,
    condition:
      getCanonicalConditionValue(input.productType, input.condition) ?? input.condition,
    notes: input.notes || undefined,
    location: {
      ...createEmptyLocation(),
      address: {
        line1: input.delivery.line1,
        line2: input.delivery.line2 || undefined,
        city: input.delivery.city,
        state: input.delivery.state.toUpperCase(),
        postalCode: input.delivery.postalCode,
        country,
      },
      quantity: input.quantity,
    },
    contact: {
      firstName: input.contact.firstName,
      lastName: input.contact.lastName,
      email: input.contact.email,
      phone: input.contact.phone,
      company: input.contact.company || undefined,
    },
  };
}

function conditionProblem(input: RequestQuoteToolInput): string | null {
  const grades = getConditionsForProductType(input.productType);
  if (getCanonicalConditionValue(input.productType, input.condition)) return null;
  const allowed = grades.map((grade) => grade.value).join(", ");
  return allowed
    ? `condition must be one of: ${allowed}`
    : `${input.productType} cannot be requested through this form`;
}

export function parseRequestQuoteToolInput(
  rawInput: unknown,
): { fill: RequestQuoteFormFill } | { problem: string } {
  const parsed = requestQuoteToolInputSchema.safeParse(rawInput);
  if (!parsed.success) {
    return { problem: z.prettifyError(parsed.error) };
  }
  const badCondition = conditionProblem(parsed.data);
  if (badCondition) return { problem: badCondition };
  const fill = toRequestQuoteFormFill(parsed.data);
  const missing = computeRfqMissingFields(fill, BUY_VALIDATION);
  if (missing.length > 0) {
    return { problem: `Fix these fields: ${formatMissingFields(missing)}` };
  }
  return { fill };
}

export function createRequestQuoteTool(
  applyToForm: (fill: RequestQuoteFormFill) => void,
): ModelContextTool {
  return {
    name: REQUEST_QUOTE_TOOL_NAME,
    title: "Request a packaging quote",
    description:
      "Fills in the Repackify quote request form for buying packaging. It does not send the request: the user reviews the filled form and presses Submit themselves.",
    inputSchema: z.toJSONSchema(requestQuoteToolInputSchema),
    annotations: { consequentialHint: true },
    execute: async (rawInput): Promise<RequestQuoteToolResult> => {
      const result = parseRequestQuoteToolInput(rawInput);
      if ("problem" in result) {
        return { status: "invalid_input", message: result.problem };
      }
      applyToForm(result.fill);
      return {
        status: "awaiting_user_submit",
        message:
          "The quote request form is filled in on the page. Nothing has been sent. Ask the user to check the details and press Submit.",
      };
    },
  };
}
